import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { CommonModule } from '@angular/common';

/**
 * Barra de progreso global para transiciones de ruta — estilo YouTube/GitHub.
 *
 * Necesaria porque las rutas son lazy: en prod IIS, descargar el chunk de la ruta
 * destino tarda 100-500ms la primera vez. Sin feedback visual durante ese gap,
 * el usuario clickea un item del sidebar y "no pasa nada" hasta que el chunk baja
 * y se monta el componente.
 *
 * Estrategia:
 *  - `NavigationStart` → arma un timer de 100ms; si la nav termina antes, no se
 *    muestra nada (evita flash para rutas cacheadas/sincronas).
 *  - Si pasan los 100ms y la nav sigue en curso → muestra la barra animando
 *    width 0% → 92% asintotico (nunca llega al 100% mientras carga).
 *  - `NavigationEnd|Cancel|Error` → jump a 100% + fade out.
 *
 * Estilo: gradient con los accent vars del tema, glow tenue. z-index alto para
 * estar sobre cualquier cromo (incluido el dx-drawer).
 */
@Component({
  selector: 'app-route-progress-bar',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="route-progress"
         [class.is-active]="active()"
         [class.is-done]="done()">
      <div class="route-progress-bar"></div>
    </div>
  `,
  styles: [`
    :host {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 99999;
      pointer-events: none;
    }
    .route-progress {
      height: 3px;
      width: 100%;
      background: transparent;
      overflow: hidden;
    }
    .route-progress-bar {
      height: 100%;
      width: 0%;
      background: linear-gradient(
        90deg,
        var(--crm-accent-1, #1890ff),
        var(--crm-accent-2, #10b981)
      );
      box-shadow: 0 0 8px var(--crm-accent-1, #1890ff);
      opacity: 0;
      transition: none;
    }
    .route-progress.is-active .route-progress-bar {
      animation: routeProgressGrow 8s cubic-bezier(0.1, 0.9, 0.3, 1) forwards;
      opacity: 1;
    }
    .route-progress.is-done .route-progress-bar {
      animation: none;
      width: 100%;
      opacity: 0;
      transition:
        width 200ms ease-out,
        opacity 300ms ease-out 150ms;
    }
    @keyframes routeProgressGrow {
      0%   { width: 0%; }
      30%  { width: 60%; }
      70%  { width: 85%; }
      100% { width: 92%; }
    }
  `],
})
export class RouteProgressBarComponent {
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  /** La barra esta visible y creciendo. */
  active = signal(false);
  /** La nav termino — jump a 100% + fade out. */
  done = signal(false);

  private startTimer: ReturnType<typeof setTimeout> | null = null;
  private endTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        if (event instanceof NavigationStart) {
          this.onStart();
        } else if (
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError
        ) {
          this.onEnd();
        }
      });
  }

  private onStart() {
    this.clearTimers();
    this.done.set(false);
    // Solo mostrar la barra si la nav tarda mas de 100ms — evita flash en
    // navegaciones instantaneas (chunks cacheados, mismo modulo).
    this.startTimer = setTimeout(() => {
      this.active.set(true);
    }, 100);
  }

  private onEnd() {
    this.clearTimers();
    if (this.active()) {
      // Estaba visible → jump a 100% con fade
      this.active.set(false);
      this.done.set(true);
      this.endTimer = setTimeout(() => this.done.set(false), 500);
    } else {
      // Nav rapida (<100ms) → no llegamos a mostrar nada, nada que limpiar.
      this.done.set(false);
    }
  }

  private clearTimers() {
    if (this.startTimer) {
      clearTimeout(this.startTimer);
      this.startTimer = null;
    }
    if (this.endTimer) {
      clearTimeout(this.endTimer);
      this.endTimer = null;
    }
  }
}
